import React, { useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { BrPageContext, BrComponentContext } from "@bloomreach/react-sdk";
import "./ContentTileContainer.scss";
import BasicContentTile from "../BasicContentTile/BasicContentTile";
import { Desktop, Mobile, IsTabletOrSmallerContent } from "../../../../media-queries/MediaQueries";
import CtaLink from "../../../Global/CtaLink";
import { COMPONENT_THEME, HEADING_LEVELS } from "../../../Global/GlobalConstants";
import ArticleTile from "../ArticleTile/ArticleTile";
import ProductTile from "../ProductTile/ProductTile";
import { isAuthored } from "../../../../helpers/LinkHelper";
import SearchTile from "../SearchTile/SearchTile";
import ErrorComponent from "../../ErrorComponent/ErrorComponent";
import Title from "../../../Global/Title/Title";
import useProductCache from "../../../../hooks/useProductCache";

const TILE_ORDINALS = [
  "first",
  "second",
  "third",
  "fourth",
  "fifth",
  "sixth",
  "seventh",
  "eighth",
  "ninth",
  "tenth",
  "eleventh",
  "twelfth",
];

const STANDARD_TILE_LIMIT = 3;
const CONDENSED_TILE_LIMIT = 12;

function ContentTileContainer() {
  const page = React.useContext(BrPageContext);
  const component = React.useContext(BrComponentContext);

  const CONTENTTYPEARTICLEHEADER = "dartcontainercombrxm:ArticleHeader";
  const CONTENTTYPEPRODUCTTILE = "dartcontainercombrxm:ProductTile";
  const CONTENTTYPESEARCHFORM = "dartcontainercombrxm:SimpleSearchForm";

  const CONDENSED_CONTENT_TILE_CONTAINER = "Condensed Content Tiles";

  const TWO_ACROSS = "two_across";

  const models = component.getModels();
  const { document: documentRef, productServletURL, tileBackgroundImage, tileBorderColor, tileBackgroundColor, tileHoverBorderColor, tileMobileButtonTextDefault } = models;
  const paramsInfo = ((component.model || {}).meta || {}).paramsInfo || {};

  const isCondensed = component.model.label === CONDENSED_CONTENT_TILE_CONTAINER;
  const condensedStyles = Array.from({ length: CONDENSED_TILE_LIMIT }, (_, index) => paramsInfo[`tile${index + 1}Style`]);
  const condensedWidthOverrides = Array.from(
    { length: CONDENSED_TILE_LIMIT },
    (_, index) => paramsInfo[`tile${index + 1}MobileWidthOverride`]
  );
  const condensedMobileButtonTexts = Array.from(
    { length: CONDENSED_TILE_LIMIT },
    (_, index) => paramsInfo[`tile${index + 1}MobileButtonText`]
  );

  // Build tile style overrides from component parameters
  const tileStyleOverrides = useMemo(() => {
    const styles = {};
    if (tileBackgroundImage) {
      styles.backgroundImage = `url('${tileBackgroundImage}')`;
      styles.backgroundSize = 'cover';
      styles.backgroundPosition = 'center';
    }
    if (tileBorderColor) {
      styles.borderColor = tileBorderColor;
      styles["--tile-border-color"] = tileBorderColor;
    }
    if (tileHoverBorderColor) {
      styles["--tile-hover-border-color"] = tileHoverBorderColor;
    }
    if (tileBackgroundColor) {
      styles.backgroundColor = tileBackgroundColor;
    }
    return Object.keys(styles).length > 0 ? styles : null;
  }, [tileBackgroundImage, tileBorderColor, tileHoverBorderColor, tileBackgroundColor]);

  const minTileHeight = 120;
  const isTabletOrSmaller = IsTabletOrSmallerContent();

  const tileCodes = useMemo(
    () => TILE_ORDINALS.map((ordinal) => models[`${ordinal}ProductTileCode`]),
    [
      models.firstProductTileCode,
      models.secondProductTileCode,
      models.thirdProductTileCode,
      models.fourthProductTileCode,
      models.fifthProductTileCode,
      models.sixthProductTileCode,
      models.seventhProductTileCode,
      models.eighthProductTileCode,
      models.ninthProductTileCode,
      models.tenthProductTileCode,
      models.eleventhProductTileCode,
      models.twelfthProductTileCode,
    ]
  );
  const containerRef = useRef(null);

  // Fetch products with caching
  const areAllUndefined = tileCodes.every((prod) => prod === undefined);
  const { products } = useProductCache(
    tileCodes,
    productServletURL,
    null,
    null,
    !areAllUndefined && !!productServletURL  // Only fetch if codes exist
  );

  const resizeTiles = useCallback(() => {
    if(!isCondensed || !containerRef.current) return;
    var maxTileHeight = minTileHeight

    // Remove max height (so clientHeight will read the real height)
    for(let i = 0 ; i < containerRef.current.children.length ; i++) {
      const child = containerRef.current.children[i];
      child.style["min-height"] = null;
    }

    // Only enforce max height on > tablet
    if(isTabletOrSmaller) {
      return;
    }

    // Find max height
    for(let i = 0 ; i < containerRef.current.children.length ; i++) {
      const child = containerRef.current.children[i];
      if (maxTileHeight < child.clientHeight) {
        maxTileHeight = child.clientHeight;
      }
    }

    // Apply max height
    for(let i = 0 ; i < containerRef.current.children.length ; i++) {
      const child = containerRef.current.children[i];
      child.style["min-height"] = "" + maxTileHeight + "px";
    }
  }, [isCondensed, isTabletOrSmaller, minTileHeight]);

  useLayoutEffect(()=> {
    resizeTiles()
    window.addEventListener('resize', resizeTiles);
    return () => window.removeEventListener('resize', resizeTiles);
  }, [isTabletOrSmaller, resizeTiles]);

  try {
    const document = page.getContent(documentRef);
    if (!document) return null;

    const { componentStyle } = component.getParameters();
    const theme = COMPONENT_THEME.find((e) => e.ComponentStyle === componentStyle);

    const documentData = document.getData();
    const { title = null, callToAction = null } = documentData;
    const maxTilesToRender = isCondensed ? CONDENSED_TILE_LIMIT : STANDARD_TILE_LIMIT;
    const tileEntries = TILE_ORDINALS.map((ordinal, index) => {
      const tileDocument = documentData[`${ordinal}TileDocument`] || null;
      const imageHeightOverride = documentData[`${ordinal}TileImageHeightOverride`] ?? null;
      const imageWidthOverride = documentData[`${ordinal}TileImageWidthOverride`] ?? null;

      if (products[index]) {
        products[index].height = imageHeightOverride;
        products[index].width = imageWidthOverride;
      }

      if (tileDocument) {
        tileDocument.height = imageHeightOverride;
        tileDocument.width = imageWidthOverride;
      }

      return {
        tileDocument,
        product: products[index],
        condensedStyle: condensedStyles[index],
        tileWidthOverride: condensedWidthOverrides[index],
        tileMobileButtonText: condensedMobileButtonTexts[index],
      };
    })
      .slice(0, maxTilesToRender)
      .filter(({ tileDocument }) => !!tileDocument);

    const tileWidthHint = (isCondensed && tileEntries.length === 2) ? TWO_ACROSS : "";

    const showCta = isAuthored(callToAction);

    function getContentType(ref, product = undefined, condensedStyle, tileWidthOverride, tileStyleOverrides, tileMobileButtonText) {
      const tileDocument = page.getContent(ref);
      const { contentType = null } = tileDocument.getData();

      if (contentType === CONTENTTYPEARTICLEHEADER) {
        return (
          <ArticleTile
            tile={tileDocument}
            componentStyle={theme.ComponentStyle}
            height={ref.height}
            width={ref.width}
            tileStyleOverrides={tileStyleOverrides}
          />
        );
      } else if (contentType === CONTENTTYPEPRODUCTTILE) {
        if (product === undefined) {
          return null;
        }
        return (
          <ProductTile
            tile={tileDocument}
            product={product}
            componentStyle={theme.ComponentStyle}
            tileStyleOverrides={tileStyleOverrides}
          />
        );
      } else if (contentType === CONTENTTYPESEARCHFORM) {
        return (
          <SearchTile
            tile={tileDocument}
            componentStyle={theme.ComponentStyle}
            isCondensed={isCondensed}
            tileStyleOverrides={tileStyleOverrides}
          />
        );
      } else {
        return (
          <BasicContentTile
            tile={tileDocument}
            componentStyle={theme.ComponentStyle}
            basicHeight={ref.height}
            basicWidth={ref.width}
            isCondensed={isCondensed}
            condensedStyle={condensedStyle}
            tileWidthOverride={tileWidthOverride}
            tileStyleOverrides={tileStyleOverrides}
            tileMobileButtonText={tileMobileButtonText}
            tileMobileButtonTextDefault={tileMobileButtonTextDefault}
          />
        );
      }
    }

    return (
      <div className={`tile-container theme-${theme.ComponentStyle}`}>
        <div className="tile-container__wrapper">
          <div className="tile-container__header">
            {title && (
              <Title
                defaultLevel={HEADING_LEVELS.TWO}
                className={`tile-container__heading--${theme.ComponentStyle}`}
              >
                {title}
              </Title>
            )}
            {showCta && (
              <Desktop>
                <div className="tile-container__cta-wrapper">
                  <CtaLink
                    link={callToAction}
                    className={`tile-container__cta ${theme.CtaClasses} btn--secondary`}
                  />
                </div>
              </Desktop>
            )}
          </div>

          <div className={`tile-container__tiles ${tileWidthHint} ${isCondensed ? "condensed" : ""}`} ref={containerRef}>
            {tileEntries.map(({ tileDocument, product, condensedStyle, tileWidthOverride, tileMobileButtonText }, index) => (
              <React.Fragment key={`${tileDocument.id || tileDocument._meta?.deliveryId || index}`}>
                {getContentType(tileDocument, product, condensedStyle, tileWidthOverride, tileStyleOverrides, tileMobileButtonText)}
              </React.Fragment>
            ))}
          </div>

          {showCta && (
            <Mobile>
              <div className="tile-container__cta-wrapper">
                <CtaLink
                  link={callToAction}
                  className={`tile-container__cta ${theme.CtaClasses} btn--secondary`}
                />
              </div>
            </Mobile>
          )}
        </div>
      </div>
    );
  } catch (e) {
    console.error("ContentTileContainer error", e);
    return <ErrorComponent error={e} componentName={"ContentTileContainer"} />;
  }
}

export default ContentTileContainer;
